import AttachmentStrip from 'components/Widget/AttachmentStrip';
import Icon, { IconType } from 'components/Icon';
import MarkdownText from 'components/Widget/MarkdownText';
import Pill from 'components/Widget/Pill';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import useDsNavigation from 'hooks/useDsNavigation';
import { Attachment, DsPick } from 'dragonmouse-shared/interfaces';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import * as S from './styles';

interface ResolvedDsPick {
	pick: DsPick;
	name: string;
}

interface UserMessageProps {
	message: string;
	isMobile?: boolean;
	attachments?: Attachment[];
	sourceFrameIds?: string[];
	resolvedDsPicks?: ResolvedDsPick[];
	onContextMenu?: (e: React.MouseEvent) => void;
}

const UserMessage = ({
	message,
	isMobile,
	attachments,
	sourceFrameIds,
	resolvedDsPicks,
	onContextMenu,
}: UserMessageProps) => {
	const theme = useTheme();
	const { openPickTab } = useDsNavigation();
	const [isCopied, setIsCopied] = useState(false);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onPickClick = (pick: DsPick) => {
		openPickTab(pick);
	};

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(message);
			setIsCopied(true);
			setTimeout(() => setIsCopied(false), 1500);
		} catch {
			toast.error('Failed to copy');
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderDsPicks = () => {
		if (!resolvedDsPicks?.length) return null;
		return (
			<S.DsPicks>
				{resolvedDsPicks.map(({ pick, name }) => (
					<Pill
						key={getReactKey(pick)}
						label={name}
						tooltip="Click to open in design system"
						onClick={() => onPickClick(pick)}
					/>
				))}
			</S.DsPicks>
		);
	};

	const renderAttachments = () => {
		if (!attachments?.length) return null;
		return <AttachmentStrip attachments={attachments} />;
	};

	const renderThumbnails = () => {
		if (!sourceFrameIds?.length) return null;
		return (
			<S.Thumbnails
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{sourceFrameIds.map((frameId) => (
					<Thumbnail
						key={frameId}
						frameId={frameId}
						size={ThumbnailSize.CHAT}
						data-frame-id={frameId}
						selectOnClick
					/>
				))}
			</S.Thumbnails>
		);
	};

	return (
		<S.Base className="user-message" isMobile={isMobile}>
			<S.Bubble isMobile={isMobile} onContextMenu={onContextMenu}>
				{renderDsPicks()}
				{renderAttachments()}
				{renderThumbnails()}
				<MarkdownText onContextMenu={onContextMenu}>{message}</MarkdownText>
			</S.Bubble>
			<S.ActionBar>
				<S.CopyButton onClick={onCopy}>
					<Icon
						type={isCopied ? IconType.CHECK : IconType.COPY}
						width={12}
						height={12}
						color={isCopied ? theme.gray[1000] : theme.gray[600]}
					/>
					{isCopied ? 'Copied' : ''}
				</S.CopyButton>
			</S.ActionBar>
		</S.Base>
	);
};

// Stable key from DsPick for rendering
function getReactKey(pick: DsPick): string {
	if (pick.kind === 'token') return `token-${pick.cssVariable}`;
	if (pick.kind === 'recipe') return `recipe-${pick.className}`;
	if (pick.kind === 'component') return `comp-${pick.tagName}`;
	if (pick.kind === 'guideline') return `guideline-${pick.guidelinePath}`;
	if (pick.kind === 'gallery') return `gallery-${pick.galleryPath}`;
	if (pick.kind === 'section') return `section-${pick.section}`;
	return `asset-${pick.assetPath}`;
}

export default UserMessage;
